// ViewpointService (§7, §10-§16). Vytvoří BCF viewpoint z aktuálního stavu vieweru
// (kamera, výběr, viditelnost, řezy, snímek) a umí ho zpět aplikovat — viewpoint
// funguje jako prostorová záložka do IFC modelu. Komunikuje jen přes ViewerAdapter.
import { createViewpoint } from "../core/model.js";

async function blobToDataURL(blob) {
  if (!blob) return null;
  return await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(blob); });
}

export class ViewpointService {
  constructor({ adapter, resolver, events }) {
    this.adapter = adapter;
    this.resolver = resolver;
    this.events = events;
  }

  // Zachytí aktuální stav vieweru do viewpointu (§7).
  async captureViewpoint({ snapshot = true } = {}) {
    const camera = this.adapter.getCamera();
    const selection = this.adapter.getSelectedComponents();
    const visibility = this.adapter.getVisibility();
    const clippingPlanes = this.adapter.getClippingPlanes();
    let snap = null;
    if (snapshot) {
      try { const blob = await this.adapter.captureSnapshot(); snap = { type: "png", fileName: "snapshot.png", data: await blobToDataURL(blob) }; }
      catch (_) { snap = null; }
    }
    const vp = createViewpoint({ camera, selection, visibility, clippingPlanes, snapshot: snap });
    this.events && this.events.emit("bcf.viewpoint.created", { viewpoint: vp });
    return vp;
  }

  // Aplikuje viewpoint zpět na viewer (§16). Vrátí i report o nenalezených prvcích (§29).
  async applyViewpoint(vp) {
    if (!vp) return { camera: false, resolution: null };
    if (vp.camera) this.adapter.setCamera(vp.camera);

    this.adapter.setClippingPlanes(vp.clippingPlanes || []);

    // Viditelnost.
    if (vp.visibility) this.adapter.applyVisibility({
      defaultVisibility: vp.visibility.defaultVisibility !== false ? vp.visibility.defaultVisibility : false,
      exceptions: this._resolveRefs(vp.visibility.exceptions || []),
    });

    // Výběr + zvýraznění relevantních prvků.
    const selResolved = this._resolveRefs(vp.selection || []);
    this.adapter.clearSelection();
    if (selResolved.length) await this.adapter.selectComponents(selResolved);

    // Barvení.
    if (vp.coloring && vp.coloring.length) await this.adapter.applyColoring(vp.coloring.map((c) => ({ color: c.color, components: this._resolveRefs(c.components || []) })));

    const resolution = this.resolver ? this.resolver.resolveMany([].concat(vp.selection || [], (vp.visibility && vp.visibility.exceptions) || [])) : null;
    this.events && this.events.emit("bcf.viewpoint.applied", { viewpoint: vp, resolution });
    return { camera: !!vp.camera, resolution };
  }

  _resolveRefs(refs) {
    if (!this.resolver) return refs;
    // Ponecháme jen existující (resolved) — chybějící prvky viewer přeskočí (§29).
    const norm = this.resolver.normalizeRefs(refs);
    return norm.filter((r) => this.resolver.resolveOne(r) === "resolved");
  }
}
